import { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useGlobal } from 'reactn';
import gql from 'graphql-tag';
import { Switch as AntSwitch } from 'antd';
import Card from '../../common/components/Card';
import DateRangePicker from '../../common/components/DateRangePicker';
import Button from '../../common/components/Button';
import TimeOffReportGrid from './TimeOffReportGrid';
import TextLoader from '../../common/components/TextLoader';
import { fetchTimesheetsForPeriod } from '../index';
import { createGridData } from './TimesheetDailySnapshotContainer';

export const GET_TIMEOFF_SNAPSHOT = gql`
  query getTimesheetWeeklySnapshot($startDate: String, $endDate: String) {
    finance {
      timesheetSnapshots(startDate: $startDate, endDate: $endDate, timeOff: true)
    }
  }
`;

let exportFunc;

const handleExportClick = () => exportFunc();

const onExportReady = ({ getExportFunc }) => {
  exportFunc = getExportFunc();
};

const slugify = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const formatTaskDateForCollapsed = value =>
  value ? moment(value, ['YYYY-MM-DD', 'YYYYMMDD', 'MM-DD-YYYY']).format('M/D') : '';

const buildDeliverableList = rows => {
  const byName = new Map();
  const usedFields = new Set();

  rows.forEach(row => {
    const name = row.deliverable;
    if (!name || byName.has(name)) return;
    const baseSlug = slugify(name) || 'deliverable';
    let slug = baseSlug;
    let counter = 2;
    while (usedFields.has(`deliverable_${slug}`) || usedFields.has(`total_${slug}`)) {
      slug = `${baseSlug}_${counter}`;
      counter += 1;
    }
    const field = `deliverable_${slug}`;
    const totalField = `total_${slug}`;
    usedFields.add(field);
    usedFields.add(totalField);
    byName.set(name, { name, field, totalField });
  });

  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const collapseTimeOffRows = (rows, deliverables) => {
  const deliverableByName = new Map(deliverables.map(d => [d.name, d]));
  const grouped = new Map();

  rows.forEach(row => {
    const key = row.timesheetId || `${row.email || ''}-${row.endDate || ''}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        timesheetId: row.timesheetId,
        endDate: row.endDate,
        status: row.status,
        name: row.name,
        email: row.email,
        _datesByField: {},
        _totalsByField: {},
      });
    }

    const entry = grouped.get(key);
    const deliverable = deliverableByName.get(row.deliverable);
    if (!deliverable) return;

    const rawDate = row.taskdate;
    if (rawDate) {
      if (!entry._datesByField[deliverable.field]) {
        entry._datesByField[deliverable.field] = new Set();
      }
      entry._datesByField[deliverable.field].add(rawDate);
    }

    if (row.hours) {
      entry._totalsByField[deliverable.totalField] =
        (entry._totalsByField[deliverable.totalField] || 0) + row.hours;
    }
  });

  return Array.from(grouped.values()).map(entry => {
    const row = {
      timesheetId: entry.timesheetId,
      endDate: entry.endDate,
      status: entry.status,
      name: entry.name,
      email: entry.email,
    };

    deliverables.forEach(deliverable => {
      const dates = entry._datesByField[deliverable.field];
      if (dates && dates.size > 0) {
        row[deliverable.field] = Array.from(dates)
          .sort()
          .map(formatTaskDateForCollapsed)
          .join(', ');
      }
      const total = entry._totalsByField[deliverable.totalField];
      if (total) {
        row[deliverable.totalField] = total;
      }
    });

    return row;
  });
};

// const constructGridData = timesheetSnapshots => {
//   const timesheets = timesheetSnapshots.reduce((acc, cur) => {
//     const email = cur.user.emails[0].address;

//     const projects = cur.lineItems
//       .reduce((accL, curL) => {
//         const totalLineItemHours = sumLineItemHours(curL);
//         const existingProject = accL.find(p => p.name === curL.project.name);
//         if (existingProject) {
//           existingProject.totalLineItemHours += totalLineItemHours;
//           return accL;
//         }
//         return [
//           ...accL,
//           {
//             ...curL.project,
//             totalLineItemHours,
//           },
//         ];
//       }, [])
//       .map(p => ({
//         ...p,
//         allocation: p.totalLineItemHours / cur.totalHours,
//       }));

//     return {
//       ...acc,
//       [email]: {
//         ...cur,
//         projects,
//       },
//     };
//   }, {});

//   let gridRows = [];
//   for (const key in timesheets) {
//     const ts = timesheets[key];
//     const totalByLineItems = timesheets[key].projects.reduce(
//       (acc, cur) => acc + cur.totalLineItemHours,
//       0,
//     );

//     ts.projects.forEach(p => {
//       gridRows.push({
//         firstName: ts.user.firstName,
//         lastName: ts.user.lastName,
//         email: ts.user.emails[0].address,
//         payrollId: ts.user.payrollId,
//         totalHours: p.totalLineItemHours,
//         allocation: numeral(p.totalLineItemHours / totalByLineItems).format('0.00%'),
//         project: p.name,
//         bu: `${p.accountingCode || ''}-${p.accountingCodeSuffix || ''}`,
//         endDate: moment(ts.endDate).format('MM-DD-YYYY'),
//       });
//     });
//   }
//   return gridRows;

//   // return data?.finance?.timesheetSnapshots.map(ts => ({
//   //   firstName: ts.user.firstName,
//   //   lastName: ts.user.lastName,
//   //   email: ts.user.emails[0].address,
//   //   totalHours: ts.totalHours,
//   // }));
// };

const TimeOffReport = () => {
  const [endDate] = useGlobal('endDate');
  const [dateRange, setDateRange] = useState([
    moment(endDate).subtract(7, 'days'),
    moment(endDate),
  ]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const fetchedData = await fetchTimesheetsForPeriod({
        startDate: dateRange[0],
        endDate: dateRange[1],
        setLoadingText,
        query: GET_TIMEOFF_SNAPSHOT,
      });
      setTimesheets(fetchedData);
      setLoading(false);
    };

    fetchData().catch(console.error);
  }, [dateRange]);

  const gridData = createGridData(timesheets).flat();
  const deliverables = useMemo(() => buildDeliverableList(gridData), [gridData]);
  const collapsedRowData = useMemo(
    () => collapseTimeOffRows(gridData, deliverables),
    [gridData, deliverables],
  );
  const exportEndDate = useMemo(() => moment(dateRange[1]).format('MM-DD-YYYY'), [dateRange]);

  return (
    <Card
      border
      title="Time Off Report"
      floating
      padded={false}
      actionComponent={
        <div className="flex">
          <DateRangePicker
            loading={loading}
            handleDateChange={range => {
              setDateRange(range);
            }}
            startDate={dateRange[0]}
            endDate={dateRange[1]}
          />
          <div className="pl-2">
            <Button disabled={loading} onClick={handleExportClick}>
              Export
            </Button>
          </div>
          <div className="pl-4 flex items-center gap-2">
            <AntSwitch
              title="Collapse rows (per user)"
              checked={collapsed}
              onChange={setCollapsed}
            />
            <span>Collapse rows (per user)</span>
          </div>
        </div>
      }
    >
      {loading ? (
        <TextLoader text={loadingText} />
      ) : (
        <TimeOffReportGrid
          onExportReady={onExportReady}
          rowData={collapsed ? collapsedRowData : gridData}
          collapsed={collapsed}
          deliverables={deliverables}
          endDate={exportEndDate}
        />
      )}
    </Card>
  );
};

export default TimeOffReport;
